
import { useMemo, useState } from "react";
import {
  HandCoins,
  ChartNoAxesCombined,
  ChartPie,
  Search,
} from "lucide-react";


import "../styles/CustomerFinancialReport.css";

const numberFormat = new Intl.NumberFormat("fa-IR");

const formatNumber = (value) =>
  numberFormat.format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("fa-IR");
};

export default function CustomerFinancialReport({
  orders = [],
  summary = {},
}) {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);

  const pageSize = 8;

  const totalPrice = summary?.totalPrice ?? 0;
  const totalScore = summary?.totalScore ?? 0;
  const totalShareAmount = summary?.totalShareAmount ?? 0;

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesSearch = [
        order.id,
        order.amount,
        order.score,
        order.share_amount,
        formatDate(order.created_at),
      ].some((value) =>
        String(value ?? "").toLowerCase().includes(query)
      );

      const matchesType =
        type === "all" || String(order.type) === type;

      return matchesSearch && matchesType;
    });
  }, [orders, search, type]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages);

  const visibleOrders = filteredOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const updateSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const updateType = (value) => {
    setType(value);
    setPage(1);
  };

  return (
    <section className="customer-financial-report" dir="rtl">
      {/* Summary cards */}
      <div className="cfr-summary">

         <SummaryCard
          icon={<ChartPie />}
          title="مبلغ کل فروش"
          value={totalPrice}
        />
     

        <SummaryCard
          icon={<ChartNoAxesCombined />}
          title="جمع کل امتیاز"
          value={totalScore}
          isMoney={false}
        />
   <SummaryCard
          icon={<HandCoins />}
          title="جمع کل سهم مشاور"
          value={totalShareAmount}
        />
       
      </div>

      {/* Search and filter */}
      <div className="cfr-toolbar">
        <label className="cfr-search">
          <span>جستجو:</span>

          <div className="cfr-search-input">
            <Search size={16} />

            <input
              type="search"
              value={search}
              placeholder="جستجو در سفارش‌ها..."
              onChange={(event) =>
                updateSearch(event.target.value)
              }
            />
          </div>
        </label>

        {/* <label className="cfr-filter">
          <span>نوع سفارش:</span>

          <select
            value={type}
            onChange={(event) =>
              updateType(event.target.value)
            }
          >
            <option value="all">همه</option>
            <option value="0">خرید محصول</option>
            <option value="1">سفارش امتیازی</option>
          </select>
        </label> */}
      </div>

      {/* Orders table */}
      <div className="cfr-table-wrapper">
        <table className="cfr-table">
          <thead>
            <tr>
              <th>#</th>
              <th>کد سفارش</th>
              <th>تاریخ سفارش</th>
              <th>نوع سفارش</th>
              <th>مبلغ</th>
              <th>امتیاز</th>
              <th>مبلغ سهم مشاور</th>
            </tr>
          </thead>

          <tbody>
            {visibleOrders.map((order, index) => (
              <tr key={order.id}>
                <td>
                  {filteredOrders.length -
                    ((currentPage - 1) * pageSize + index)}
                </td>

                <td>{order.id}</td>

                <td>{formatDate(order.created_at)}</td>

                <td>
                  <span className="cfr-order-badge">
                    {String(order.type) === "0"
                      ? "خرید محصول"
                      : "سفارش امتیازی"}
                  </span>
                </td>

                <td>{formatNumber(order.amount)}</td>

                <td>
                  <span className="cfr-score-badge">
                    {formatNumber(order.score)}
                  </span>
                </td>

                <td>
                  {formatNumber(order.share_amount)}
                </td>
              </tr>
            ))}

            {visibleOrders.length === 0 && (
              <tr>
                <td colSpan={7} className="cfr-empty">
                  سفارشی برای نمایش وجود ندارد.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="cfr-footer">
        <span>
          نمایش{" "}
          {filteredOrders.length === 0
            ? 0
            : (currentPage - 1) * pageSize + 1}
          {" تا "}
          {Math.min(
            currentPage * pageSize,
            filteredOrders.length
          )}
          {" از "}
          {filteredOrders.length} رکورد
        </span>

        <div className="cfr-pagination">
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            بعدی
          </button>

          <span>{formatNumber(currentPage)}</span>

          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            قبلی
          </button>
        </div>
      </div>
    </section>
  );
}

function SummaryCard({
  icon,
  title,
  value,
  isMoney = true,
}) {
  return (
    <article className="cfr-card">
      <div className="cfr-card-icon">{icon}</div>

      <div className="cfr-card-content">
        <span>{title}</span>

        <strong>
          {formatNumber(value)}

          {isMoney && (
            <small className="cfr-currency"> تومان</small>
          )}
        </strong>
      </div>
    </article>
  );
}
